import { useCallback, useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import type { ConnectorTokenStatusResponse } from '@awwyeah/shared';
import {
  connectorUrlFromToken,
  fetchConnectorTokenStatus,
  mintConnectorToken,
  revokeConnectorToken,
} from './connectorApi';

function relativeTime(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * "Connect Claude" settings card (WF-1.2, docs/MCP-CONNECTOR-PLAN.md D1) — mints/revokes
 * this member's personal connector URL and shows how to add it to Claude. Styling, testid
 * and copy-once conventions cloned closely from `CalendarFeedSection`'s WS-22 card, one
 * section below it in Settings: the URL is shown exactly once, right after minting (only
 * the SHA-256 hash is persisted, mirroring session cookies), so "regenerate" is what you
 * reach for if you lose it or think it leaked.
 */
export function ConnectClaudeSection(): JSX.Element {
  const [status, setStatus] = useState<ConnectorTokenStatusResponse | 'loading'>('loading');
  const [mintedUrl, setMintedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    fetchConnectorTokenStatus()
      .then(setStatus)
      .catch(() => setStatus({ exists: false, createdAt: null, lastUsedAt: null }));
  }, []);

  useEffect(() => refresh(), [refresh]);

  const generate = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const token = await mintConnectorToken();
      setMintedUrl(connectorUrlFromToken(token));
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate a connector link.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await revokeConnectorToken();
      setMintedUrl(null);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not turn off the connector link.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (): Promise<void> => {
    if (!mintedUrl) return;
    try {
      await navigator.clipboard.writeText(mintedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select and copy the link manually.');
    }
  };

  const exists = status !== 'loading' && status.exists;

  return (
    <section
      className="space-y-3 rounded-lg border border-line p-4"
      data-testid="settings-connect-claude"
    >
      <h2 className="text-lg font-semibold text-fg">Connect Claude</h2>
      <p className="text-xs leading-snug text-fg-3">
        Get a private link that lets your Claude (Desktop, claude.ai, or on your phone) see and
        add to the family calendar as you.
      </p>

      {mintedUrl && (
        <div className="space-y-1 rounded-md border border-line bg-bg-2 p-2.5">
          <p className="text-xs font-medium text-fg-2">
            Copy this now — treat it like a password, and it won't be shown again.
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={mintedUrl}
              data-testid="connector-url"
              onFocus={(e) => e.currentTarget.select()}
              className="h-10 min-w-0 flex-1 truncate rounded-md border border-line bg-bg px-2 text-xs text-fg"
            />
            <button
              type="button"
              onClick={() => void copy()}
              data-testid="connector-copy"
              className="flex h-10 min-w-[44px] shrink-0 items-center justify-center rounded-md border border-line px-2 text-fg-2"
              aria-label="Copy connector link"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-1 text-sm">
        <Row label="Status" value={status === 'loading' ? 'Checking…' : exists ? 'On' : 'Off'} />
        {exists && status.lastUsedAt && (
          <Row label="Last used" value={relativeTime(status.lastUsedAt)} />
        )}
        {exists && !status.lastUsedAt && (
          <Row label="Last used" value="Not yet — paste the link into Claude to start" />
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          onClick={() => void generate()}
          disabled={busy}
          data-testid="connector-generate"
          className="min-h-[44px] rounded-xl bg-inverse px-4 py-2 text-sm font-semibold text-inverse-fg disabled:opacity-60"
        >
          {busy ? 'Working…' : exists ? 'Regenerate link' : 'Generate link'}
        </button>
        {exists && (
          <button
            type="button"
            onClick={() => void revoke()}
            disabled={busy}
            data-testid="connector-revoke"
            className="min-h-[44px] rounded-xl border border-line px-4 py-2 text-sm font-semibold text-fg-2 disabled:opacity-60"
          >
            Turn off
          </button>
        )}
      </div>
      {error && <p className="text-sm text-danger">{error}</p>}

      <details className="pt-1 text-sm text-fg-2">
        <summary className="cursor-pointer select-none font-medium">How to add it</summary>
        <div className="mt-2 space-y-2 text-xs leading-snug text-fg-3">
          <p>
            In Claude (Desktop, claude.ai, or the app): Settings → Connectors → Add custom
            connector → paste the link above. No sign-in needed — the link itself is what proves
            it's you.
          </p>
          <p>
            Once it's added, your Claude can answer "what's the family doing this weekend?" and
            add things like "dinner at Grandma's Sunday 5pm" straight to the calendar.
          </p>
        </div>
      </details>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-fg-3">{label}</span>
      <span className="font-medium text-fg">{value}</span>
    </div>
  );
}
